import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { Spinner } from "@components/common/spinner/spinner";
import { ProjectCreationContext } from "@pages/create-project/project-creation-context";
import { useEffect, useState } from "react";
import { useAppNavigation } from "@router/use-app-navigation";
import { useCreateProject } from "@hooks/use-create-project";
import { CreateProjectPage } from "@pages/create-project/create-project-page";
import { PlanCreationDialog } from "@pages/create-project/plan-creation-dialog";
import { CompanyProjectCreation } from "@pages/create-project/project-creation-context-types";
import { ProjectSummaryPage } from "@pages/create-project/project-summary-page";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { useBlockNavigation } from "@router/use-block-navigation";

interface Props {
  /** The subject of the project which it belongs to */
  subject: CompanyProjectCreation;
}

/**
 * Manage the general project creation logics and states and update the related context
 */
export function CreateProjectContainer({
  subject,
}: Props): React.JSX.Element | null {
  const { setShouldBlockNavigation } = useBlockNavigation({
    alertMessage: "You have unsaved changes. Are you sure you want to leave?",
  });
  const [projectName, setProjectName] = useState<string>("");
  const [projectAddress, setProjectAddress] = useState<string>("");
  const [projectArea, setProjectArea] = useState<number | null>(0);
  const [projectAreaUnit, setProjectAreaUnit] =
    useState<FaroCoreApiTypes.AreaUnit>("sqft");

  // If new project is for a member, initial value is set to member's email as this value should not be changed
  const [projectManager, setProjectManager] = useState<string>("");

  const [isFetchingData, setIsFetchingData] = useState<boolean>(false);
  const [shouldShowSummary, setShouldShowSummary] = useState<boolean>(false);
  const [isStandalone, setIsStandalone] = useState<boolean>(false);

  const { handleErrorWithDialog } = useErrorHandler();
  const { navigateToProfilePage } = useAppNavigation();

  const {
    isRequestingProjectCreation,
    requestCreateProject,
    createdProjectId,
  } = useCreateProject({ errorHandler: handleErrorWithDialog });

  // Update fetching state based on project creation request state
  useEffect(() => {
    setIsFetchingData(isRequestingProjectCreation);
  }, [isRequestingProjectCreation]);

  if (isFetchingData) {
    return <Spinner />;
  }

  const createProjectPayload = {
    name: projectName,
    address: projectAddress,
    area: {
      amount: projectArea ?? 0,
      unit: projectAreaUnit,
    },
    projectManager,
    // eslint-disable-next-line @typescript-eslint/naming-convention -- backend variable definition
    standalone: isStandalone,
  };

  // Navigate to the members's or company's profile page after creation of a non-standalone project
  if (createdProjectId && !isStandalone) {
    navigateToProfilePage({
      subjectType: subject.type,
      subjectId: subject.company.id,
    });
    return null;
  }

  let content: React.JSX.Element;
  if (createdProjectId) {
    content = <PlanCreationDialog />;
  } else {
    content = shouldShowSummary ? (
      <ProjectSummaryPage
        onConfirmCreation={() => {
          setShouldBlockNavigation(false);
          void requestCreateProject({
            createProjectPayload,
            subject,
          });
        }}
      />
    ) : (
      <CreateProjectPage />
    );
  }

  return (
    <ProjectCreationContext.Provider
      value={{
        subject,
        projectName,
        setProjectName,
        projectAddress,
        setProjectAddress,
        projectArea,
        setProjectArea,
        projectAreaUnit,
        setProjectAreaUnit,
        projectManager,
        setProjectManager,
        createdProjectId,
        isFetchingData,
        setIsFetchingData,
        isStandalone,
        setIsStandalone,
        shouldShowSummary,
        setShouldShowSummary,
      }}
    >
      {createdProjectId && <PlanCreationDialog />}

      {content}
    </ProjectCreationContext.Provider>
  );
}
